import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCreditsContext } from '@/context/CreditsContext';
import { downloadFile } from '@/lib/download';
import {
  motionOptions,
  aspectOptions,
  VIDEO_AD_EDGE_URL,
  VIDEO_AD_GENERATE_CREDITS,
  VIDEO_AD_REGENERATE_CREDITS,
  VIDEO_AD_SOUND_EFFECT_CREDITS,
  VIDEO_AD_VOICEOVER_CREDITS,
  VIDEO_AD_DEFAULT_DURATION,
  getVideoAdCreditCost,
  type VideoAdDuration,
  type VideoAdState,
} from '@/config/videoAd';
import { QuotaExceededError, InsufficientCreditsError } from '@/api/generateAds';
import { IMAGE_ENGINES, getBulkCreditCost, type ImageEngineId } from '@/config/imageEngines';
import { buildVideoPromptFromContext, buildTextControlInstruction, type SharedAdContext, type TextControlMode, type TextControlOptions } from '@/lib/videoPromptBuilder';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';
import { getSupabaseFunctionUrl } from '@/lib/supabase';

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 180;
const POLL_REQUEST_TIMEOUT_MS = 30000;
const MAX_RENDER_WAIT_MS_BY_DURATION: Record<VideoAdDuration, number> = {
  5: 6 * 60 * 1000,
  10: 12 * 60 * 1000,
};

// ─── Generation Mode ──────────────────────────────────────────────────────────
// "image"        → generate a creative image only (no video)
// "video-only"   → text-to-video using gen4.5 (no image step)
// "image+video"  → generate image first, then animate it with gen4
// "upload+video" → user uploads their own image, animate it with gen4
type GenerationMode = 'image' | 'video-only' | 'image+video' | 'upload+video';
type VideoAudioMode = 'none' | 'voiceover' | 'sound_effect';
type VoiceStyle = 'friendly' | 'energetic' | 'luxury' | 'calm' | 'bold';
type VoiceScriptMode = 'auto' | 'custom';
type SoundEffectStyle = 'whoosh' | 'pop' | 'cash' | 'camera' | 'crowd' | 'arcade' | 'chime' | 'custom';

interface CampaignForm {
  offer: string;
  campaignGoal: string;
  audienceOverride: string;
  platform: string;
  toneOverride: string;
  adAngle: string;
  campaignNotes: string;
  creativeDirection: string;
  stylePackIds: string[];
  imageCount: number;
}

interface BusinessProfileData {
  id: string;
  business_name: string;
  business_type: string;
  business_description?: string;
  services_offered?: string;
  default_audience?: string;
  default_tone?: string;
  location?: string;
  brand_positioning?: string;
  unique_differentiator?: string;
}

export interface GenerateVideoAdModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessProfile: BusinessProfileData | null;
  campaign: CampaignForm;
  imageEngine: ImageEngineId;
  referenceImageData: string | null;
  styleLabel?: string;
}

type GenerationPhase = 'configure' | 'image' | 'video' | 'completed' | 'failed';
type PromptMode = 'smart' | 'custom';

interface VideoAudioOptions {
  mode: VideoAudioMode;
  voiceStyle: VoiceStyle;
  scriptMode: VoiceScriptMode;
  customScript: string;
  soundEffectStyle: SoundEffectStyle;
  customSoundEffect: string;
}

/* ─── Context Summary Card ─────────────────────────────────────────────────── */
function ContextSummaryCard({
  ctx,
  styleLabel,
}: {
  ctx: SharedAdContext;
  styleLabel: string;
}) {
  const items = [
    { icon: 'ri-building-2-line', label: 'Business', value: ctx.businessName || ctx.industry || '—' },
    { icon: 'ri-price-tag-3-line', label: 'Offer', value: ctx.offer || '—' },
    { icon: 'ri-focus-3-line', label: 'Audience', value: ctx.targetAudience || '—' },
    { icon: 'ri-palette-line', label: 'Style', value: styleLabel || 'Auto Mix' },
    { icon: 'ri-flag-2-line', label: 'Goal', value: ctx.adGoal || '—' },
  ].filter(item => item.value !== '—');

  if (items.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-link-m text-orange-500 text-sm" />
        </div>
        <span className="text-xs font-bold text-orange-800 uppercase tracking-wider">Using Your Existing Ad Details</span>
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {items.map(item => (
          <div key={item.label} className="flex items-start gap-2.5">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className={`${item.icon} text-orange-400 text-xs`} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-orange-700">{item.label}: </span>
              <span className="text-xs text-stone-600 break-words">{item.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Mode Selector ────────────────────────────────────────────────────────── */
function ModeSelectorCard({
  mode,
  selected,
  onClick,
}: {
  mode: GenerationMode;
  selected: boolean;
  onClick: () => void;
}) {
  const config: Record<GenerationMode, { icon: string; title: string; subtitle: string; badge?: string; model?: string }> = {
    'image': {
      icon: 'ri-image-line',
      title: 'Creative Image',
      subtitle: 'Generate a high-quality ad image from your campaign data',
      badge: 'Image only',
    },
    'video-only': {
      icon: 'ri-film-line',
      title: 'Text to Video',
      subtitle: 'Generate a video directly from your campaign prompt — no image needed',
    },
    'image+video': {
      icon: 'ri-movie-2-line',
      title: 'Image + Video',
      subtitle: 'Generate an image first, then animate it into a video ad',
    },
    'upload+video': {
      icon: 'ri-upload-cloud-2-line',
      title: 'Upload Image → Video',
      subtitle: 'Upload your own image and animate it into a video ad',
    },
  };

  const c = config[mode];

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
        selected
          ? 'border-orange-400 bg-orange-50'
          : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
      }`}
    >
      <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 transition-all ${
        selected ? 'border-orange-500 bg-orange-500' : 'border-stone-300'
      }`}>
        {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
      </div>
      <div className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
        selected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
      }`}>
        <i className={`${c.icon} text-base`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-bold ${selected ? 'text-orange-800' : 'text-stone-800'}`}>
            {c.title}
          </span>
          {c.badge && (
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              selected ? 'bg-orange-200 text-orange-700' : 'bg-stone-200 text-stone-500'
            }`}>
              {c.badge}
            </span>
          )}
        </div>
        <p className={`text-xs mt-0.5 leading-tight ${selected ? 'text-orange-500' : 'text-stone-400'}`}>
          {c.subtitle}
        </p>
      </div>
    </button>
  );
}

/* ─── Main Modal ───────────────────────────────────────────────────────────── */
export default function GenerateVideoAdModal({
  isOpen,
  onClose,
  businessProfile,
  campaign,
  imageEngine,
  referenceImageData,
  styleLabel = '',
}: GenerateVideoAdModalProps) {
  const { user } = useAuth();
  const { creditsRemaining, hasEnoughCredits, refetch: refetchCredits, fireToast } = useCreditsContext();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<GenerationPhase>('configure');
  const [generationMode, setGenerationMode] = useState<GenerationMode>('video-only');
  const [promptMode, setPromptMode] = useState<PromptMode>('smart');
  const [selectedMotion, setSelectedMotion] = useState<'morph' | 'dynamic' | 'cinematic'>('dynamic');
  const [selectedAspect, setSelectedAspect] = useState<'vertical' | 'square' | 'landscape'>('vertical');

  const [smartPrompt, setSmartPrompt] = useState('');
  const [isEditingSmartPrompt, setIsEditingSmartPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [textControl, setTextControl] = useState<TextControlMode>('off');
  const [exactText, setExactText] = useState('');
  const [videoDuration, setVideoDuration] = useState<VideoAdDuration>(VIDEO_AD_DEFAULT_DURATION);

  // Image generation state
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // Video generation state
  const [videoState, setVideoState] = useState<VideoAdState>({
    status: 'idle',
    taskId: null,
    videoUrl: null,
    audioUrl: null,
    voiceoverScript: null,
    error: null,
    progress: null,
  });
  const [audioOptions, setAudioOptions] = useState<VideoAudioOptions>({
    mode: 'none',
    voiceStyle: 'friendly',
    scriptMode: 'auto',
    customScript: '',
    soundEffectStyle: 'whoosh',
    customSoundEffect: '',
  });
  const [voiceoverDraft, setVoiceoverDraft] = useState('');
  const [isRegeneratingVoiceover, setIsRegeneratingVoiceover] = useState(false);
  const [voiceoverEditError, setVoiceoverEditError] = useState<string | null>(null);

  // Track whether credits were refunded after a failure
  const [creditsRefunded, setCreditsRefunded] = useState(false);

  // Image-only result
  const [imageOnlyUrl, setImageOnlyUrl] = useState<string | null>(null);

  // Upload+video: user-uploaded image (stored as data URL)
  const [uploadedImageDataUrl, setUploadedImageDataUrl] = useState<string | null>(null);

  const pollCountRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);
  const generateVideoRef = useRef<((imageUrl: string | null, genId: string | null) => Promise<void>) | null>(null);

  // Build shared context from props
  const sharedCtx: SharedAdContext = {
    businessName: businessProfile?.business_name ?? '',
    industry: businessProfile?.business_type ?? '',
    offer: campaign.offer,
    targetAudience: campaign.audienceOverride || businessProfile?.default_audience || '',
    tone: campaign.toneOverride || businessProfile?.default_tone || '',
    style: styleLabel || 'Auto Mix',
    creativeDirection: campaign.creativeDirection,
    adGoal: campaign.campaignGoal,
    adAngle: campaign.adAngle,
    platform: campaign.platform,
    location: businessProfile?.location,
    businessDescription: businessProfile?.business_description,
    servicesOffered: businessProfile?.services_offered,
    brandPositioning: businessProfile?.brand_positioning,
    uniqueDifferentiator: businessProfile?.unique_differentiator,
  };

  const hasContext = Boolean(
    sharedCtx.businessName || sharedCtx.industry || sharedCtx.offer || sharedCtx.targetAudience
  );

  useEffect(() => {
    if (isOpen && hasContext) {
      const generated = buildVideoPromptFromContext(sharedCtx, { textControl, exactText });
      setSmartPrompt(generated);
    }
  }, [isOpen, businessProfile?.id, campaign.offer, campaign.campaignGoal, campaign.adAngle, campaign.toneOverride, campaign.audienceOverride, textControl, exactText]);

  useEffect(() => {
    if (!isOpen) {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      setPhase('configure');
      setPromptMode('smart');
      setIsEditingSmartPrompt(false);
      setGeneratedImageUrl(null);
      setGenerationId(null);
      setImageError(null);
      setImageOnlyUrl(null);
      setUploadedImageDataUrl(null);
      setVideoState({ status: 'idle', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
      setAudioOptions({ mode: 'none', voiceStyle: 'friendly', scriptMode: 'auto', customScript: '', soundEffectStyle: 'whoosh', customSoundEffect: '' });
      setVideoDuration(VIDEO_AD_DEFAULT_DURATION);
      pollStartedAtRef.current = null;
      setVoiceoverDraft('');
      setIsRegeneratingVoiceover(false);
      setVoiceoverEditError(null);
      setIsGeneratingImage(false);
      setCreditsRefunded(false);
      setTextControl('off');
      setExactText('');
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Credit costs
  const imageCreditCost = getBulkCreditCost(imageEngine, 1);
  const videoCreditCost = getVideoAdCreditCost(videoDuration);
  const audioCreditCost =
    generationMode === 'image' ? 0 :
    audioOptions.mode === 'voiceover' ? VIDEO_AD_VOICEOVER_CREDITS :
    audioOptions.mode === 'sound_effect' ? VIDEO_AD_SOUND_EFFECT_CREDITS :
    0;

  // Total cost depends on mode
  const totalCreditCost =
    generationMode === 'image' ? imageCreditCost :
    generationMode === 'video-only' ? videoCreditCost + audioCreditCost :
    generationMode === 'upload+video' ? videoCreditCost + audioCreditCost : // only video cost, no image gen
    imageCreditCost + videoCreditCost + audioCreditCost; // image+video

  const canAffordTotal = hasEnoughCredits(totalCreditCost);
  const canGenerateUpload = generationMode !== 'upload+video' || !!uploadedImageDataUrl;

  const textControlOptions: TextControlOptions = { textControl, exactText };

  // For custom prompts, append the text control instruction manually.
  // Smart prompts already have it baked in via buildVideoPromptFromContext.
  const activePrompt = promptMode === 'custom'
    ? `${customPrompt.trim()} ${buildTextControlInstruction(textControlOptions)}`.trim()
    : smartPrompt.trim();

  // ── Generate image (used for "image" and "image+video" modes) ──────────────
  const generateImage = useCallback(async (retryCount = 0) => {
    if (!businessProfile) return;

    setPhase('image');
    setIsGeneratingImage(true);
    setImageError(null);

    try {
      const API_URL = getSupabaseFunctionUrl('generate-ads');

      const payload = {
        businessName: businessProfile.business_name,
        businessType: businessProfile.business_type,
        businessDescription: businessProfile.business_description,
        audience: campaign.audienceOverride || businessProfile.default_audience,
        offer: campaign.offer,
        platform: campaign.platform,
        tone: campaign.toneOverride || businessProfile.default_tone,
        campaignGoal: campaign.campaignGoal,
        campaignNotes: campaign.campaignNotes,
        businessProfileId: businessProfile.id,
        servicesOffered: businessProfile.services_offered,
        location: businessProfile.location,
        brandPositioning: businessProfile.brand_positioning,
        uniqueDifferentiator: businessProfile.unique_differentiator,
        adAngle: campaign.adAngle || undefined,
        creativeDirection: campaign.creativeDirection || undefined,
        stylePack: campaign.stylePackIds[0] || 'auto',
        stylePackIds: campaign.stylePackIds.length > 0 ? campaign.stylePackIds : undefined,
        imageCount: 1,
        referenceImageBase64: referenceImageData ?? undefined,
        imageEngine,
        provider: IMAGE_ENGINES.find(e => e.id === imageEngine)?.provider || 'fal',
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.status === 403) {
        const body = await response.json().catch(() => ({})) as Record<string, unknown>;
        if (body?.error === 'INSUFFICIENT_CREDITS') {
          throw new InsufficientCreditsError(typeof body.creditsRemaining === 'number' ? body.creditsRemaining : 0);
        }
        throw new QuotaExceededError();
      }

      if (response.status === 429) {
        const body = await response.json().catch(() => ({})) as Record<string, unknown>;
        if (body?.error === 'PRO_BULK_DAILY_CAP_EXCEEDED') {
          const dailyCap = typeof body.dailyCap === 'number' ? body.dailyCap : 3;
          const used = typeof body.used === 'number' ? body.used : 3;
          throw new Error(`Daily generation limit reached (${used}/${dailyCap}). Please try again tomorrow.`);
        }
        if (body?.error === 'PRO_BULK_COOLDOWN') {
          const remainingMin = typeof body.remainingMin === 'number' ? body.remainingMin : 10;
          throw new Error(`Please wait ${remainingMin} minutes before generating more images.`);
        }
        if (retryCount < 3) {
          const delayMs = Math.pow(2, retryCount) * 2000;
          await new Promise(resolve => setTimeout(resolve, delayMs));
          return generateImage(retryCount + 1);
        }
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      }

      if (!response.ok) {
        throw new Error(`Image generation failed (${response.status}). Please try again.`);
      }

      const data = await response.json() as Record<string, unknown>;
      if (data?.error) throw new Error(String(data.error));

      const storageUrls = Array.isArray(data.imageStorageUrls)
        ? (data.imageStorageUrls as string[]).filter(Boolean)
        : [];
      const rawUrls = Array.isArray(data.imageUrls)
        ? (data.imageUrls as string[]).filter(Boolean)
        : [];
      const imageUrl = storageUrls[0] || rawUrls[0] || null;

      if (!imageUrl) {
        throw new Error('The image service did not return an image. Please try again.');
      }

      const generationIdFromApi = typeof data.generationId === 'string' ? data.generationId : null;

      // ── Image-only mode: done here ──────────────────────────────────────────
      if (generationMode === 'image') {
        setImageOnlyUrl(imageUrl);
        setGenerationId(generationIdFromApi);
        setPhase('completed');
        await refetchCredits();
        fireToast('Creative image generated!', 'deduct', imageCreditCost);
        return;
      }

      // ── Image+Video mode: proceed to video ──────────────────────────────────
      setGeneratedImageUrl(imageUrl);
      setGenerationId(generationIdFromApi);
      setPhase('video');
      fireToast('Image generated — starting video animation', 'deduct', imageCreditCost);
      setTimeout(() => generateVideoRef.current?.(imageUrl, generationIdFromApi), 500);

    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        setImageError('Insufficient credits to generate image.');
      } else if (err instanceof QuotaExceededError) {
        setImageError('You have reached your generation limit.');
      } else {
        setImageError(err instanceof Error ? err.message : 'Failed to generate image. Please try again.');
      }
      setPhase('failed');
    } finally {
      setIsGeneratingImage(false);
    }
  }, [businessProfile, campaign, imageEngine, referenceImageData, imageCreditCost, user?.id, fireToast, generationMode, refetchCredits]);

  const callEdge = useCallback(async (payload: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), POLL_REQUEST_TIMEOUT_MS);
    const res = await fetch(VIDEO_AD_EDGE_URL, {
      method: 'POST',
      headers: await getEdgeFunctionHeaders(),
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    try {
      const data = await res.json() as Record<string, unknown>;
      if (!res.ok || data.error) throw new Error(typeof data.error === 'string' ? data.error : `Request failed (${res.status})`);
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  const refundTimedOutVideo = useCallback(async (taskId: string, genId: string | null) => {
    try {
      const data = await callEdge({
        action: 'timeout_refund',
        taskId,
        generationId: genId,
        duration: videoDuration,
        audioOptions: audioOptions.mode === 'voiceover'
          ? {
              mode: audioOptions.mode,
              voiceStyle: audioOptions.voiceStyle,
              scriptMode: audioOptions.scriptMode,
              customScript: audioOptions.customScript.trim() || undefined,
            }
          : audioOptions.mode === 'sound_effect'
          ? {
              mode: audioOptions.mode,
              soundEffectStyle: audioOptions.soundEffectStyle,
              customSoundEffect: audioOptions.customSoundEffect.trim() || undefined,
            }
          : { mode: 'none' },
      });
      const wasRefunded = data.creditsRefunded === true;
      setCreditsRefunded(wasRefunded);
      if (wasRefunded) await refetchCredits();
    } catch {
      setCreditsRefunded(false);
    }
  }, [audioOptions, callEdge, refetchCredits, videoDuration]);

  const pollTask = useCallback(async (taskId: string, genId: string | null) => {
    const startedAt = pollStartedAtRef.current ?? Date.now();
    pollStartedAtRef.current = startedAt;
    const elapsedMs = Date.now() - startedAt;
    const maxWaitMs = MAX_RENDER_WAIT_MS_BY_DURATION[videoDuration];

    if (pollCountRef.current >= MAX_POLLS || elapsedMs >= maxWaitMs) {
      await refundTimedOutVideo(taskId, genId);
      setVideoState((prev) => ({
        ...prev,
        status: 'failed',
        error: `Video generation timed out after ${Math.round(maxWaitMs / 60000)} minutes. Your AdForge credits were refunded if this task had not already completed.`,
      }));
      setPhase('failed');
      return;
    }

    try {
      const data = await callEdge({
        action: 'poll',
        taskId,
        generationId: genId,
        duration: videoDuration,
        sharedAdContext: sharedCtx,
        businessProfileId: businessProfile?.id ?? null,
        audioOptions: audioOptions.mode === 'voiceover'
          ? {
              mode: audioOptions.mode,
              voiceStyle: audioOptions.voiceStyle,
              scriptMode: audioOptions.scriptMode,
              customScript: audioOptions.customScript.trim() || undefined,
            }
          : audioOptions.mode === 'sound_effect'
          ? {
              mode: audioOptions.mode,
              soundEffectStyle: audioOptions.soundEffectStyle,
              customSoundEffect: audioOptions.customSoundEffect.trim() || undefined,
            }
          : { mode: 'none' },
      });
      const status = data.status as string;

      if (status === 'completed') {
        const returnedGenerationId = typeof data.generationId === 'string' ? data.generationId : null;
        if (returnedGenerationId) setGenerationId(returnedGenerationId);
        const returnedScript = typeof data.voiceoverScript === 'string' ? data.voiceoverScript : null;
        setVideoState({
          status: 'completed',
          taskId,
          videoUrl: data.videoUrl as string,
          audioUrl: typeof data.audioUrl === 'string' ? data.audioUrl : null,
          voiceoverScript: returnedScript,
          error: null,
          progress: 100,
        });
        setVoiceoverDraft(returnedScript ?? '');
        setVoiceoverEditError(null);
        setCreditsRefunded(false);
        setPhase('completed');
        await refetchCredits();
        fireToast(
          audioOptions.mode === 'voiceover'
            ? 'Video ad and voiceover generated!'
            : audioOptions.mode === 'sound_effect'
            ? 'Video ad and sound effect generated!'
            : 'Video ad generated!',
          'deduct',
          videoCreditCost + audioCreditCost,
        );
      } else if (status === 'failed') {
        const wasRefunded = data.creditsRefunded === true;
        const failureMessage = typeof data.failureMessage === 'string'
          ? data.failureMessage
          : 'Video generation failed. Please try again.';
        setCreditsRefunded(wasRefunded);
        if (wasRefunded) {
          // Refresh the credit balance so the UI reflects the refund immediately
          await refetchCredits();
        }
        setVideoState((prev) => ({
          ...prev,
          status: 'failed',
          error: failureMessage,
        }));
        setPhase('failed');
      } else {
        pollCountRef.current += 1;
        const progress = typeof data.progress === 'number' ? data.progress : null;
        setVideoState((prev) => ({ ...prev, status: 'processing', progress }));
        pollTimerRef.current = setTimeout(() => pollTask(taskId, genId), POLL_INTERVAL_MS);
      }
    } catch (err) {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      await refundTimedOutVideo(taskId, genId);
      setVideoState((prev) => ({
        ...prev,
        status: 'failed',
        error: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      }));
      await refetchCredits();
      setPhase('failed');
    }
  }, [callEdge, refetchCredits, fireToast, user?.id, sharedCtx, businessProfile?.id, audioOptions, audioCreditCost, videoCreditCost, videoDuration, refundTimedOutVideo]);

  // ── Generate video (image-to-video with gen4, or text-to-video with gen4.5) ─
  const generateVideo = useCallback(async (imageUrl: string | null, genId: string | null) => {
    // imageUrl is null for text-to-video (gen4.5), present for image-to-video (gen4)
    setVideoState({ status: 'pending', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
    pollCountRef.current = 0;
    pollStartedAtRef.current = Date.now();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

    try {
      const payload: Record<string, unknown> = {
        action: 'create',
        motionStyle: selectedMotion,
        aspectRatio: selectedAspect,
        duration: videoDuration,
        provider: 'runway',
        generationType: 'video_ad',
        generationId: genId,
        creativePrompt: activePrompt || undefined,
        sharedAdContext: sharedCtx,
        businessProfileId: businessProfile?.id ?? null,
        audioOptions: audioOptions.mode === 'voiceover'
          ? {
              mode: audioOptions.mode,
              voiceStyle: audioOptions.voiceStyle,
              scriptMode: audioOptions.scriptMode,
              customScript: audioOptions.customScript.trim() || undefined,
            }
          : audioOptions.mode === 'sound_effect'
          ? {
              mode: audioOptions.mode,
              soundEffectStyle: audioOptions.soundEffectStyle,
              customSoundEffect: audioOptions.customSoundEffect.trim() || undefined,
            }
          : { mode: 'none' },
        // Model selection is automatic in the edge function:
        // - sourceImageUrl present → gen4 (image-to-video)
        // - sourceImageUrl absent  → gen4.5 (text-to-video)
      };

      // Only include sourceImageUrl when we have an image (triggers gen4)
      if (imageUrl) {
        payload.sourceImageUrl = imageUrl;
      }

      const data = await callEdge(payload);

      const taskId = data.taskId as string;
      setVideoState((prev) => ({ ...prev, status: 'processing', taskId }));
      pollTimerRef.current = setTimeout(() => pollTask(taskId, genId), POLL_INTERVAL_MS);
    } catch (err) {
      setVideoState({
        status: 'failed',
        taskId: null,
        videoUrl: null,
        audioUrl: null,
        voiceoverScript: null,
        error: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
        progress: null,
      });
      setPhase('failed');
    }
  }, [selectedMotion, selectedAspect, callEdge, pollTask, user?.id, activePrompt, sharedCtx, businessProfile?.id, audioOptions, videoDuration]);

  useEffect(() => {
    generateVideoRef.current = generateVideo;
  }, [generateVideo]);

  // ── Main generate handler — routes based on mode ──────────────────────────
  const handleGenerate = useCallback(() => {
    if (generationMode === 'image') {
      generateImage();
    } else if (generationMode === 'video-only') {
      // Text-to-video: skip image step, go straight to video with no sourceImageUrl
      setPhase('video');
      generateVideo(null, null);
    } else if (generationMode === 'upload+video') {
      // User-uploaded image → animate with gen4
      if (!uploadedImageDataUrl) return;
      setGeneratedImageUrl(uploadedImageDataUrl);
      setPhase('video');
      generateVideo(uploadedImageDataUrl, null);
    } else {
      // image+video: generate image first, then animate
      generateImage();
    }
  }, [generationMode, generateImage, generateVideo, uploadedImageDataUrl]);

  const handleRegenerateVideo = useCallback(async () => {
    if (generationMode === 'video-only') {
      setPhase('video');
      await generateVideo(null, generationId);
    } else if (generationMode === 'upload+video' && uploadedImageDataUrl) {
      setPhase('video');
      await generateVideo(uploadedImageDataUrl, generationId);
    } else if (generatedImageUrl) {
      setPhase('video');
      await generateVideo(generatedImageUrl, generationId);
    }
  }, [generationMode, generatedImageUrl, generationId, generateVideo, uploadedImageDataUrl]);

  const handleRegenerateVoiceover = useCallback(async (script: string) => {
    const cleanedScript = script.trim();
    if (!cleanedScript || !videoState.videoUrl || isRegeneratingVoiceover) return;

    setIsRegeneratingVoiceover(true);
    setVoiceoverEditError(null);
    try {
      const data = await callEdge({
        action: 'regenerate_voiceover',
        taskId: videoState.taskId,
        generationId,
        duration: videoDuration,
        sharedAdContext: sharedCtx,
        audioOptions: {
          mode: 'voiceover',
          voiceStyle: audioOptions.voiceStyle,
          scriptMode: 'custom',
          customScript: cleanedScript,
        },
      });

      const audioUrl = typeof data.audioUrl === 'string' ? data.audioUrl : null;
      const voiceoverScript = typeof data.voiceoverScript === 'string' ? data.voiceoverScript : cleanedScript;
      setVideoState(prev => ({
        ...prev,
        audioUrl,
        voiceoverScript,
      }));
      setVoiceoverDraft(voiceoverScript);
      await refetchCredits();
      fireToast('Voiceover regenerated!', 'deduct', VIDEO_AD_VOICEOVER_CREDITS);
    } catch (err) {
      setVoiceoverEditError(err instanceof Error ? err.message : 'Voiceover regeneration failed. Please try again.');
    } finally {
      setIsRegeneratingVoiceover(false);
    }
  }, [audioOptions.voiceStyle, callEdge, fireToast, generationId, isRegeneratingVoiceover, refetchCredits, sharedCtx, videoDuration, videoState.taskId, videoState.videoUrl]);

  const handleStartOver = useCallback(() => {
    setPhase('configure');
    setGeneratedImageUrl(null);
    setImageOnlyUrl(null);
    setVideoState({ status: 'idle', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
    pollStartedAtRef.current = null;
    setImageError(null);
    setVoiceoverDraft('');
    setVoiceoverEditError(null);
  }, []);

  const handleDownloadVideo = () => {
    if (!videoState.videoUrl) return;
    downloadFile(videoState.videoUrl, `adforge-video-ad-${Date.now()}.mp4`);
  };

  const handleDownloadImage = () => {
    if (!imageOnlyUrl) return;
    downloadFile(imageOnlyUrl, `adforge-image-ad-${Date.now()}.jpg`);
  };

  const handleClose = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    onClose();
  };

  if (!isOpen) return null;

  const isActive =
    (phase === 'image' && isGeneratingImage) ||
    (phase === 'video' && (videoState.status === 'pending' || videoState.status === 'processing'));

  // Phase step: 0=configure, 1=image/video-processing, 2=done
  const phaseStep =
    phase === 'configure' ? 0 :
    (phase === 'image' || phase === 'video') ? 1 : 2;

  const headerTitle =
    phase === 'configure' ? 'Generate Creative' :
    phase === 'image' ? 'Generating Image...' :
    phase === 'video' ? 'Animating Video...' :
    phase === 'completed' ? (generationMode === 'image' ? 'Image Ready!' : 'Video Ready!') :
    'Generation Failed';

  const headerSubtitle =
    phase === 'configure' ? 'Choose what to generate from your campaign data' :
    phase === 'image' ? 'Creating your ad image' :
    phase === 'video' ? 'AI is animating your video' :
    phase === 'completed' ? 'Your creative is ready to download' :
    'Something went wrong';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !isActive && handleClose()}
      />

      <div className="relative bg-white rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-400 shrink-0">
              <i className="ri-film-fill text-white text-base" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-stone-900 leading-tight">{headerTitle}</h2>
              <p className="text-xs text-stone-400 mt-0.5">{headerSubtitle}</p>
            </div>
          </div>
          {!isActive && (
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5 px-6 pt-4 shrink-0">
          {[0, 1, 2].map((step) => (
            <div
              key={step}
              className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
                phaseStep > step ? 'bg-orange-400' :
                phaseStep === step && step > 0 ? 'bg-orange-300 animate-pulse' :
                'bg-stone-200'
              }`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-5 space-y-5">

          {/* ── Configure Phase ── */}
          {phase === 'configure' && (
            <ConfigureView
              sharedCtx={sharedCtx}
              styleLabel={styleLabel}
              hasContext={hasContext}
              generationMode={generationMode}
              setGenerationMode={setGenerationMode}
              promptMode={promptMode}
              setPromptMode={setPromptMode}
              smartPrompt={smartPrompt}
              setSmartPrompt={setSmartPrompt}
              isEditingSmartPrompt={isEditingSmartPrompt}
              setIsEditingSmartPrompt={setIsEditingSmartPrompt}
              customPrompt={customPrompt}
              setCustomPrompt={setCustomPrompt}
              selectedMotion={selectedMotion}
              setSelectedMotion={setSelectedMotion}
              selectedAspect={selectedAspect}
              setSelectedAspect={setSelectedAspect}
              videoDuration={videoDuration}
              setVideoDuration={setVideoDuration}
              textControl={textControl}
              setTextControl={setTextControl}
              exactText={exactText}
              setExactText={setExactText}
              audioOptions={audioOptions}
              setAudioOptions={setAudioOptions}
              imageCreditCost={imageCreditCost}
              videoCreditCost={videoCreditCost}
              audioCreditCost={audioCreditCost}
              totalCreditCost={totalCreditCost}
              canAffordTotal={canAffordTotal}
              creditsRemaining={creditsRemaining}
              businessProfile={businessProfile}
              uploadedImageDataUrl={uploadedImageDataUrl}
              setUploadedImageDataUrl={setUploadedImageDataUrl}
              canGenerateUpload={canGenerateUpload}
              onGenerate={handleGenerate}
            />
          )}

          {/* ── Image Generating Phase ── */}
          {phase === 'image' && (
            <div className="flex flex-col items-center justify-center py-10 gap-5 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 border-2 border-orange-100">
                <i className="ri-image-line text-orange-500 text-2xl animate-pulse" />
              </div>
              <div>
                <p className="text-base font-extrabold text-stone-900">Generating your ad image...</p>
                <p className="text-sm text-stone-400 mt-1.5 max-w-xs mx-auto leading-relaxed">
                  {generationMode === 'image+video'
                    ? 'Creating the perfect image, then we\'ll animate it into a video.'
                    : 'Using your campaign data to create a high-quality ad image.'}
                </p>
              </div>
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Video Processing Phase ── */}
          {phase === 'video' && (
            <VideoProcessingView
              imageUrl={generationMode === 'video-only' ? null : generatedImageUrl}
              videoState={videoState}
              isTextToVideo={generationMode === 'video-only'}
              hasVoiceover={audioOptions.mode === 'voiceover'}
              hasSoundEffect={audioOptions.mode === 'sound_effect'}
              videoDuration={videoDuration}
            />
          )}

          {/* ── Completed Phase (image-only) ── */}
          {phase === 'completed' && generationMode === 'image' && imageOnlyUrl && (
            <ImageCompletedView
              imageUrl={imageOnlyUrl}
              onDownload={handleDownloadImage}
              onStartOver={handleStartOver}
            />
          )}

          {/* ── Completed Phase (video) ── */}
          {phase === 'completed' && generationMode !== 'image' && videoState.videoUrl && (
            <VideoCompletedView
              videoUrl={videoState.videoUrl}
              audioUrl={videoState.audioUrl}
              voiceoverScript={videoState.voiceoverScript}
              audioMode={audioOptions.mode}
              voiceoverDraft={voiceoverDraft}
              setVoiceoverDraft={setVoiceoverDraft}
              onRegenerateVoiceover={handleRegenerateVoiceover}
              isRegeneratingVoiceover={isRegeneratingVoiceover}
              voiceoverEditError={voiceoverEditError}
              imageUrl={generationMode === 'image+video' ? generatedImageUrl : null}
              motionLabel={motionOptions.find(m => m.id === selectedMotion)?.label || ''}
              aspectLabel={aspectOptions.find(a => a.id === selectedAspect)?.label || ''}
              aspectRatio={aspectOptions.find(a => a.id === selectedAspect)?.ratio || '9:16'}
              isTextToVideo={generationMode === 'video-only'}
              onDownload={handleDownloadVideo}
              onRegenerateVideo={handleRegenerateVideo}
              onStartOver={handleStartOver}
              onOpenAudioStudio={() => {
                handleClose();
                const arMap: Record<string, string> = { vertical: '9:16', square: '1:1', landscape: '16:9' };
                navigate('/dashboard/audio-studio', {
                  state: {
                    videoUrl: videoState.videoUrl,
                    audioUrl: videoState.audioUrl,
                    voiceoverScript: videoState.voiceoverScript,
                    generationId,
                    businessName: sharedCtx.businessName,
                    businessType: sharedCtx.industry,
                    offer: sharedCtx.offer,
                    platform: sharedCtx.platform,
                    audience: sharedCtx.targetAudience,
                    tone: sharedCtx.tone,
                    aspectRatio: arMap[selectedAspect] ?? '9:16',
                  },
                });
              }}
              onEditVideo={() => {
                handleClose();
                const arMap: Record<string, string> = { vertical: '9:16', square: '1:1', landscape: '16:9' };
                navigate('/dashboard/video-editor', {
                  state: {
                    videoUrl: videoState.videoUrl,
                    audioUrl: videoState.audioUrl,
                    voiceoverScript: videoState.voiceoverScript,
                    aspectRatio: arMap[selectedAspect] ?? '9:16',
                    videoId: String(Date.now()),
                  },
                });
              }}
              canAffordRegen={hasEnoughCredits(VIDEO_AD_REGENERATE_CREDITS)}
              creditsRemaining={creditsRemaining}
            />
          )}

          {/* ── Failed Phase ── */}
          {phase === 'failed' && (
            <FailedView
              error={imageError || videoState.error}
              onRetry={imageError ? generateImage : handleRegenerateVideo}
              onStartOver={handleStartOver}
              onClose={handleClose}
              isImageError={!!imageError}
              canAfford={hasEnoughCredits(imageError ? imageCreditCost : VIDEO_AD_REGENERATE_CREDITS)}
              creditsRefunded={!imageError && creditsRefunded}
              refundedAmount={videoCreditCost + audioCreditCost}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Text In Video Control ────────────────────────────────────────────────── */
function TextInVideoControl({
  textControl,
  setTextControl,
  exactText,
  setExactText,
}: {
  textControl: TextControlMode;
  setTextControl: (v: TextControlMode) => void;
  exactText: string;
  setExactText: (v: string) => void;
}) {
  const options: { value: TextControlMode; label: string; description: string; icon: string }[] = [
    {
      value: 'off',
      label: 'Off',
      description: 'Recommended — no text, signs, or lettering in the video',
      icon: 'ri-eye-off-line',
    },
    {
      value: 'english_text',
      label: 'AI English Text',
      description: 'Allow real, legible English words only — no gibberish',
      icon: 'ri-text',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest">
          Text in Video
        </label>
        <span className="text-xs text-stone-400 font-medium">
          Control AI-generated text &amp; signage
        </span>
      </div>

      {/* Segmented toggle */}
      <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1">
        {options.map((opt) => {
          const isSelected = textControl === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setTextControl(opt.value)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isSelected
                  ? 'bg-white text-stone-900'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${opt.icon} text-xs ${isSelected ? 'text-orange-500' : ''}`} />
              </div>
              {opt.label}
              {opt.value === 'off' && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  isSelected ? 'bg-orange-100 text-orange-600' : 'bg-stone-200 text-stone-400'
                }`}>
                  Rec.
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Description of selected option */}
      <p className="text-xs text-stone-400 mt-2 leading-relaxed flex items-start gap-1.5">
        <i className="ri-information-line text-stone-300 shrink-0 mt-0.5" />
        {options.find(o => o.value === textControl)?.description}
      </p>

      {/* Exact text input — only shown when english_text is selected */}
      {textControl === 'english_text' && (
        <div className="mt-3 space-y-1.5">
          <label className="block text-xs font-semibold text-stone-600">
            Exact Text to Include
            <span className="ml-1.5 text-stone-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={exactText}
            onChange={(e) => setExactText(e.target.value.slice(0, 80))}
            placeholder='Example: The Last Level'
            className="w-full rounded-xl border-2 border-stone-200 bg-stone-50 px-4 py-2.5 text-xs text-stone-700 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-400 leading-relaxed">
              AI will attempt to include this exact text in the video.
            </p>
            <span className="text-xs text-stone-400 shrink-0">{exactText.length}/80</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Image Upload Zone ────────────────────────────────────────────────────── */
function ImageUploadZone({
  uploadedImageDataUrl,
  setUploadedImageDataUrl,
}: {
  uploadedImageDataUrl: string | null;
  setUploadedImageDataUrl: (v: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processFile = (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        setUploadedImageDataUrl(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  return (
    <div>
      <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
        Your Image
      </label>

      {uploadedImageDataUrl ? (
        <div className="relative rounded-xl overflow-hidden border-2 border-orange-300 bg-stone-50">
          <img
            src={uploadedImageDataUrl}
            alt="Uploaded"
            className="w-full max-h-52 object-contain object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            <span className="text-xs font-bold text-white flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill text-green-400" />
              Image ready to animate
            </span>
            <button
              onClick={() => setUploadedImageDataUrl(null)}
              className="text-xs font-bold text-white/80 hover:text-white bg-black/40 hover:bg-black/60 px-2.5 py-1 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1" />
              Change
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-3 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
            isDragging
              ? 'border-orange-400 bg-orange-50'
              : 'border-stone-300 bg-stone-50 hover:border-orange-300 hover:bg-orange-50/40'
          }`}
        >
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-colors ${
            isDragging ? 'bg-orange-100' : 'bg-stone-100'
          }`}>
            <i className={`ri-upload-cloud-2-line text-2xl ${isDragging ? 'text-orange-500' : 'text-stone-400'}`} />
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-stone-700">
              {isDragging ? 'Drop your image here' : 'Upload your image'}
            </p>
            <p className="text-xs text-stone-400 mt-1">
              Drag &amp; drop or click to browse · JPG, PNG, WebP · Max 10MB
            </p>
          </div>
          <span className="text-xs font-semibold text-orange-500 bg-orange-50 border border-orange-200 px-3 py-1.5 rounded-lg">
            Browse Files
          </span>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1.5">
          <i className="ri-error-warning-line shrink-0" />
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <p className="text-xs text-stone-400 mt-2 leading-relaxed flex items-start gap-1.5">
        <i className="ri-information-line text-stone-300 shrink-0 mt-0.5" />
        Best results with clear, high-quality photos.
      </p>
    </div>
  );
}

/* ─── Configure View ───────────────────────────────────────────────────────── */
function ConfigureView({
  sharedCtx,
  styleLabel,
  hasContext,
  generationMode,
  setGenerationMode,
  promptMode,
  setPromptMode,
  smartPrompt,
  setSmartPrompt,
  isEditingSmartPrompt,
  setIsEditingSmartPrompt,
  customPrompt,
  setCustomPrompt,
  selectedMotion,
  setSelectedMotion,
  selectedAspect,
  setSelectedAspect,
  videoDuration,
  setVideoDuration,
  textControl,
  setTextControl,
  exactText,
  setExactText,
  audioOptions,
  setAudioOptions,
  imageCreditCost,
  videoCreditCost,
  audioCreditCost,
  totalCreditCost,
  canAffordTotal,
  creditsRemaining,
  businessProfile,
  uploadedImageDataUrl,
  setUploadedImageDataUrl,
  canGenerateUpload,
  onGenerate,
}: {
  sharedCtx: SharedAdContext;
  styleLabel: string;
  hasContext: boolean;
  generationMode: GenerationMode;
  setGenerationMode: (m: GenerationMode) => void;
  promptMode: PromptMode;
  setPromptMode: (m: PromptMode) => void;
  smartPrompt: string;
  setSmartPrompt: (v: string) => void;
  isEditingSmartPrompt: boolean;
  setIsEditingSmartPrompt: (v: boolean) => void;
  customPrompt: string;
  setCustomPrompt: (v: string) => void;
  selectedMotion: 'morph' | 'dynamic' | 'cinematic';
  setSelectedMotion: (v: 'morph' | 'dynamic' | 'cinematic') => void;
  selectedAspect: 'vertical' | 'square' | 'landscape';
  setSelectedAspect: (v: 'vertical' | 'square' | 'landscape') => void;
  videoDuration: VideoAdDuration;
  setVideoDuration: (v: VideoAdDuration) => void;
  textControl: TextControlMode;
  setTextControl: (v: TextControlMode) => void;
  exactText: string;
  setExactText: (v: string) => void;
  audioOptions: VideoAudioOptions;
  setAudioOptions: (v: VideoAudioOptions) => void;
  imageCreditCost: number;
  videoCreditCost: number;
  audioCreditCost: number;
  totalCreditCost: number;
  canAffordTotal: boolean;
  creditsRemaining: number;
  businessProfile: { id: string; business_name: string } | null;
  uploadedImageDataUrl: string | null;
  setUploadedImageDataUrl: (v: string | null) => void;
  canGenerateUpload: boolean;
  onGenerate: () => void;
}) {
  const isVideoMode = generationMode === 'video-only' || generationMode === 'image+video' || generationMode === 'upload+video';
  const isUploadMode = generationMode === 'upload+video';
  const previousAudioModeRef = useRef<VideoAudioMode>(audioOptions.mode);

  useEffect(() => {
    const switchedToVoiceover = previousAudioModeRef.current !== 'voiceover' && audioOptions.mode === 'voiceover';
    if (isVideoMode && switchedToVoiceover && videoDuration === 5) {
      setVideoDuration(10);
    }
    previousAudioModeRef.current = audioOptions.mode;
  }, [audioOptions.mode, isVideoMode, setVideoDuration, videoDuration]);

  return (
    <>
      {/* ── Generation Mode Selector ── */}
      <div>
        <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
          What do you want to generate?
        </label>
        <div className="space-y-2">
          {(['video-only', 'image+video', 'upload+video'] as GenerationMode[]).map((m) => (
            <ModeSelectorCard
              key={m}
              mode={m}
              selected={generationMode === m}
              onClick={() => setGenerationMode(m)}
            />
          ))}
        </div>
      </div>

      {/* ── Upload Zone (upload+video mode only) ── */}
      {isUploadMode && (
        <ImageUploadZone
          uploadedImageDataUrl={uploadedImageDataUrl}
          setUploadedImageDataUrl={setUploadedImageDataUrl}
        />
      )}

      {/* ── Shared Context Section ── */}
      {hasContext && !isUploadMode && (
        <div className="space-y-3">
          <ContextSummaryCard ctx={sharedCtx} styleLabel={styleLabel} />

          {/* Text in Video Control (video modes only) */}
          {isVideoMode && (
            <TextInVideoControl
              textControl={textControl}
              setTextControl={setTextControl}
              exactText={exactText}
              setExactText={setExactText}
            />
          )}

          {/* Prompt mode toggle */}
          <div className="flex items-center gap-2 bg-stone-100 rounded-xl p-1">
            <button
              onClick={() => setPromptMode('smart')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                promptMode === 'smart'
                  ? 'bg-white text-stone-900'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className="ri-magic-line text-orange-500" />
              Smart Prompt
            </button>
            <button
              onClick={() => setPromptMode('custom')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                promptMode === 'custom'
                  ? 'bg-white text-stone-900'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className="ri-edit-2-line" />
              Start Fresh
            </button>
          </div>

          {promptMode === 'smart' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-stone-500 flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-fill text-green-500" />
                  Auto-generated from your ad data
                </p>
                <button
                  onClick={() => setIsEditingSmartPrompt(!isEditingSmartPrompt)}
                  className="text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <i className={isEditingSmartPrompt ? 'ri-eye-line' : 'ri-edit-line'} />
                  {isEditingSmartPrompt ? 'Preview' : 'Edit Prompt'}
                </button>
              </div>
              {isEditingSmartPrompt ? (
                <textarea
                  value={smartPrompt}
                  onChange={(e) => setSmartPrompt(e.target.value.slice(0, 800))}
                  rows={5}
                  className="w-full resize-none rounded-xl border-2 border-orange-200 bg-orange-50 px-4 py-3 text-xs text-stone-700 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200 leading-relaxed"
                />
              ) : (
                <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-xs text-stone-600 leading-relaxed line-clamp-4">
                  {smartPrompt || 'Generating prompt from your ad data...'}
                </div>
              )}
            </div>
          )}

          {promptMode === 'custom' && (
            <div className="space-y-2">
              <p className="text-xs text-stone-400 leading-relaxed">
                Write your own prompt from scratch. Your existing ad data will be ignored.
              </p>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value.slice(0, 800))}
                placeholder="e.g. Create a cinematic video ad for a luxury skincare brand. Show a woman applying serum in soft morning light, with close-up product shots and a warm, aspirational feel..."
                rows={5}
                className="w-full resize-none rounded-xl border-2 border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-700 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200 leading-relaxed"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-stone-400">{customPrompt.length}/800 characters</span>
                <button
                  onClick={() => setPromptMode('smart')}
                  className="text-xs text-orange-500 hover:text-orange-600 font-semibold cursor-pointer whitespace-nowrap"
                >
                  ← Use smart prompt instead
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!hasContext && !isUploadMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-amber-500 text-base shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-800">No ad data found</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              Fill in your campaign details (business, offer, audience) in the form first, then come back to generate.
            </p>
          </div>
        </div>
      )}

      {/* ── Motion Style (video modes only) ── */}
      {isVideoMode && (
        <div>
          <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
            Motion Style
          </label>
          <div className="space-y-2">
            {motionOptions.map((opt) => {
              const isSelected = selectedMotion === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSelectedMotion(opt.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
                  }`}
                >
                  <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected ? 'border-orange-500 bg-orange-500' : 'border-stone-300'
                  }`}>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                    isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                  }`}>
                    <i className={`${opt.icon} text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${isSelected ? 'text-orange-800' : 'text-stone-800'}`}>
                        {opt.label}
                      </span>
                      {opt.badge && (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                          opt.badge === 'Premium'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 leading-tight ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                      {opt.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Aspect Ratio (video modes only) ── */}
      {isVideoMode && (
        <div>
          <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
            Aspect Ratio
          </label>
          <div className="grid grid-cols-3 gap-2">
            {aspectOptions.map((opt) => {
              const isSelected = selectedAspect === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSelectedAspect(opt.id)}
                  className={`relative flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
                  }`}
                >
                  {opt.recommended && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-bold bg-orange-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                      Recommended
                    </span>
                  )}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                    isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                  }`}>
                    <i className={`${opt.icon} text-sm`} />
                  </div>
                  <span className={`text-xs font-extrabold ${isSelected ? 'text-orange-700' : 'text-stone-700'}`}>
                    {opt.ratio}
                  </span>
                  <span className={`text-xs font-medium ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Credit Summary ── */}
      {isVideoMode && (
        <div>
          <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
            Video Duration
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                value: 5 as VideoAdDuration,
                label: '5 sec',
                title: 'Quick Clip',
                icon: 'ri-flashlight-line',
                description: 'Best for fast visuals, sound effects, or a very short hook',
              },
              {
                value: 10 as VideoAdDuration,
                label: '10 sec',
                title: 'Voiceover Ad',
                icon: 'ri-mic-line',
                description: 'More room for a natural spoken message',
              },
            ].map((opt) => {
              const isSelected = videoDuration === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVideoDuration(opt.value)}
                  className={`flex items-start gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                    isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                  }`}>
                    <i className={`${opt.icon} text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-extrabold ${isSelected ? 'text-orange-800' : 'text-stone-800'}`}>
                        {opt.label}
                      </span>
                      <span className={`text-xs font-semibold ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                        {opt.title}
                      </span>
                    </div>
                    <p className={`mt-1 text-xs leading-tight ${isSelected ? 'text-orange-600' : 'text-stone-400'}`}>
                      {opt.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {audioOptions.mode === 'voiceover' && videoDuration === 5 && (
            <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <i className="ri-error-warning-line shrink-0 mt-0.5" />
              5-second videos need an extremely short voiceover. 10 seconds will sound more natural.
            </p>
          )}
        </div>
      )}

      {isVideoMode && (
        <VideoAudioControl
          audioOptions={audioOptions}
          setAudioOptions={setAudioOptions}
          audioCreditCost={audioCreditCost}
        />
      )}

      <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-xl px-4 py-4 space-y-3">
        {generationMode === 'image' && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">Generate Image</span>
            <span className="text-xs font-bold text-stone-200">{imageCreditCost} credits</span>
          </div>
        )}
        {generationMode === 'video-only' && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">Text to Video ({videoDuration}s)</span>
            <span className="text-xs font-bold text-stone-200">{videoCreditCost} credits</span>
          </div>
        )}
        {generationMode === 'image+video' && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-400">Generate Image</span>
              <span className="text-xs font-bold text-stone-200">{imageCreditCost} credits</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-400">Animate Video ({videoDuration}s)</span>
              <span className="text-xs font-bold text-stone-200">{videoCreditCost} credits</span>
            </div>
          </>
        )}
        {generationMode === 'upload+video' && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">Animate Your Image ({videoDuration}s)</span>
            <span className="text-xs font-bold text-stone-200">{videoCreditCost} credits</span>
          </div>
        )}
        {audioCreditCost > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">Voiceover</span>
            <span className="text-xs font-bold text-stone-200">{audioCreditCost} credits</span>
          </div>
        )}
        <div className="h-px bg-white/10" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-coin-line text-amber-400 text-base" />
            <span className="text-sm font-bold text-white">Total Cost</span>
          </div>
          <span className={`text-base font-extrabold ${canAffordTotal ? 'text-amber-400' : 'text-red-400'}`}>
            {totalCreditCost} credits
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-stone-400">Your balance</span>
          <span className={`font-bold ${canAffordTotal ? 'text-stone-200' : 'text-red-400'}`}>
            {creditsRemaining} credits
          </span>
        </div>
        {!canAffordTotal && (
          <p className="text-xs text-red-300 flex items-center gap-1.5">
            <i className="ri-error-warning-line shrink-0" />
            Not enough credits. Please upgrade or purchase more credits.
          </p>
        )}
      </div>

      {/* ── Generate Button ── */}
      <button
        onClick={onGenerate}
        disabled={!canAffordTotal || (!isUploadMode && !businessProfile) || !canGenerateUpload}
        className={`w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
          canAffordTotal && (!isUploadMode ? !!businessProfile : true) && canGenerateUpload
            ? 'bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400 text-white hover:from-rose-600 hover:via-orange-600 hover:to-amber-500 cursor-pointer'
            : 'bg-stone-100 text-stone-400 cursor-not-allowed'
        }`}
      >
        <i className={`${generationMode === 'image' ? 'ri-image-line' : 'ri-film-line'} text-base`} />
        {generationMode === 'image'
          ? `Generate Image — ${totalCreditCost} Credits`
          : generationMode === 'video-only'
          ? `Generate Video — ${totalCreditCost} Credits`
          : generationMode === 'upload+video'
          ? `Animate My Image — ${totalCreditCost} Credits`
          : `Generate Image + Video — ${totalCreditCost} Credits`}
      </button>

      {!canAffordTotal && (
        <Link
          to="/pricing"
          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
        >
          <i className="ri-flashlight-fill" /> Upgrade to get more credits
        </Link>
      )}
    </>
  );
}

/* ─── Video Processing View ────────────────────────────────────────────────── */
function VideoAudioControl({
  audioOptions,
  setAudioOptions,
  audioCreditCost,
}: {
  audioOptions: VideoAudioOptions;
  setAudioOptions: (v: VideoAudioOptions) => void;
  audioCreditCost: number;
}) {
  const voiceStyles: { id: VoiceStyle; label: string; icon: string }[] = [
    { id: 'friendly', label: 'Friendly', icon: 'ri-emotion-happy-line' },
    { id: 'energetic', label: 'Energetic', icon: 'ri-flashlight-line' },
    { id: 'luxury', label: 'Luxury', icon: 'ri-vip-diamond-line' },
    { id: 'calm', label: 'Calm', icon: 'ri-leaf-line' },
    { id: 'bold', label: 'Bold', icon: 'ri-megaphone-line' },
  ];
  const soundEffectStyles: { id: SoundEffectStyle; label: string; prompt: string; icon: string }[] = [
    { id: 'whoosh', label: 'Whoosh', prompt: 'Fast clean cinematic whoosh transition with subtle impact', icon: 'ri-windy-line' },
    { id: 'pop', label: 'Pop', prompt: 'Short upbeat pop accent for a social media reveal', icon: 'ri-bubble-chart-line' },
    { id: 'cash', label: 'Cash', prompt: 'Cash register ding with bright retail sale energy', icon: 'ri-money-dollar-circle-line' },
    { id: 'camera', label: 'Camera', prompt: 'Camera shutter click with polished commercial feel', icon: 'ri-camera-line' },
    { id: 'crowd', label: 'Crowd', prompt: 'Small crowd cheer and upbeat applause swell', icon: 'ri-user-smile-line' },
    { id: 'arcade', label: 'Arcade', prompt: 'Retro arcade game coin pickup and playful 8-bit flourish', icon: 'ri-gamepad-line' },
    { id: 'chime', label: 'Chime', prompt: 'Soft premium notification chime with warm sparkle', icon: 'ri-notification-3-line' },
    { id: 'custom', label: 'Custom', prompt: '', icon: 'ri-edit-2-line' },
  ];

  const update = (patch: Partial<VideoAudioOptions>) => setAudioOptions({ ...audioOptions, ...patch });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest">
          Audio
        </label>
              {audioCreditCost > 0 && (
                <span className="text-xs font-semibold text-orange-500">+{audioCreditCost} credits</span>
              )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { id: 'none' as VideoAudioMode, label: 'No Audio', icon: 'ri-volume-mute-line' },
          { id: 'voiceover' as VideoAudioMode, label: 'Voiceover', icon: 'ri-mic-line' },
          { id: 'sound_effect' as VideoAudioMode, label: 'Sound Effect', icon: 'ri-sound-module-line' },
        ].map((opt) => {
          const isSelected = audioOptions.mode === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => update({ mode: opt.id })}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer ${
                isSelected
                  ? 'border-orange-400 bg-orange-50 text-orange-700'
                  : 'border-stone-200 bg-stone-50 text-stone-500 hover:border-orange-200'
              }`}
            >
              <i className={`${opt.icon} text-sm`} />
              {opt.label}
            </button>
          );
        })}
      </div>

      {audioOptions.mode === 'voiceover' && (
        <div className="space-y-3 bg-stone-50 border border-stone-200 rounded-xl p-3">
          <div>
            <p className="text-xs font-bold text-stone-700 mb-2">Voice Style</p>
            <div className="grid grid-cols-5 gap-1.5">
              {voiceStyles.map((style) => {
                const isSelected = audioOptions.voiceStyle === style.id;
                return (
                  <button
                    key={style.id}
                    onClick={() => update({ voiceStyle: style.id })}
                    className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                      isSelected
                        ? 'border-orange-300 bg-white text-orange-600'
                        : 'border-stone-200 bg-stone-100 text-stone-500 hover:bg-white'
                    }`}
                  >
                    <i className={`${style.icon} text-sm`} />
                    <span className="truncate max-w-full">{style.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
            {[
              { id: 'auto' as VoiceScriptMode, label: 'Auto Script' },
              { id: 'custom' as VoiceScriptMode, label: 'Custom Script' },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => update({ scriptMode: opt.id })}
                className={`flex-1 py-2 rounded-md text-xs font-bold transition-all cursor-pointer ${
                  audioOptions.scriptMode === opt.id
                    ? 'bg-white text-stone-900'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {audioOptions.scriptMode === 'custom' && (
            <div className="relative">
              <textarea
                value={audioOptions.customScript}
                onChange={(e) => update({ customScript: e.target.value.slice(0, 260) })}
                placeholder="Write a short voiceover, around 10-20 seconds..."
                rows={3}
                className="w-full resize-none rounded-lg border-2 border-stone-200 bg-white px-3 py-2.5 text-xs text-stone-700 placeholder-stone-300 focus:outline-none focus:border-orange-400 transition-all leading-relaxed"
              />
              <span className="absolute bottom-2 right-2 text-xs text-stone-300">
                {audioOptions.customScript.length}/260
              </span>
            </div>
          )}
        </div>
      )}

      {audioOptions.mode === 'sound_effect' && (
        <div className="space-y-3 bg-stone-50 border border-stone-200 rounded-xl p-3">
          <div>
            <p className="text-xs font-bold text-stone-700 mb-2">Sound Effect Style</p>
            <div className="grid grid-cols-4 gap-1.5">
              {soundEffectStyles.map((style) => {
                const isSelected = audioOptions.soundEffectStyle === style.id;
                return (
                  <button
                    key={style.id}
                    onClick={() => update({ soundEffectStyle: style.id, customSoundEffect: style.id === 'custom' ? audioOptions.customSoundEffect : style.prompt })}
                    className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                      isSelected
                        ? 'border-orange-300 bg-white text-orange-600'
                        : 'border-stone-200 bg-stone-100 text-stone-500 hover:bg-white'
                    }`}
                  >
                    <i className={`${style.icon} text-sm`} />
                    <span className="truncate max-w-full">{style.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <textarea
            value={audioOptions.customSoundEffect}
            onChange={(e) => update({ customSoundEffect: e.target.value.slice(0, 220), soundEffectStyle: 'custom' })}
            placeholder="Describe the sound, e.g. quick whoosh into a bright cash register ding..."
            rows={3}
            className="w-full resize-none rounded-lg border-2 border-stone-200 bg-white px-3 py-2.5 text-xs text-stone-700 placeholder-stone-300 focus:outline-none focus:border-orange-400 transition-all leading-relaxed"
          />
          <p className="text-xs text-stone-400 leading-relaxed">
            Best for 5-second clips: quick transitions, product reveals, sale moments, and playful brand accents.
          </p>
        </div>
      )}
    </div>
  );
}

function VideoProcessingView({
  imageUrl,
  videoState,
  isTextToVideo,
  hasVoiceover,
  hasSoundEffect,
  videoDuration,
}: {
  imageUrl: string | null;
  videoState: VideoAdState;
  isTextToVideo: boolean;
  hasVoiceover: boolean;
  hasSoundEffect: boolean;
  videoDuration: VideoAdDuration;
}) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-8 gap-6 text-center">
      {imageUrl && !isTextToVideo && (
        <div className="relative">
          <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-orange-200">
            <img src={imageUrl} alt="Source" className="w-full h-full object-cover object-top" />
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
            <i className="ri-film-fill text-white text-sm" />
          </div>
        </div>
      )}
      {isTextToVideo && (
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 border-2 border-orange-100">
          <i className="ri-film-line text-orange-500 text-2xl animate-pulse" />
        </div>
      )}
      <div>
        <p className="text-base font-extrabold text-stone-900">
          {videoState.status === 'pending' ? 'Starting animation' : 'Creating your video'}{dots}
        </p>
        <p className="text-sm text-stone-400 mt-1.5 max-w-xs mx-auto leading-relaxed">
          {isTextToVideo
            ? 'Generating your video from the text prompt. This usually takes 1–2 minutes.'
            : videoState.status === 'pending'
            ? 'Preparing your video generation task...'
            : 'Animating your image into a video. This usually takes 1–2 minutes.'}
        </p>
        {hasVoiceover && (
          <p className="text-xs text-orange-500 mt-2 font-semibold">Voiceover will be created after the video is ready.</p>
        )}
        {hasSoundEffect && (
          <p className="text-xs text-orange-500 mt-2 font-semibold">Sound effect will be created after the video is ready.</p>
        )}
        <p className="text-xs text-stone-300 mt-1 font-semibold">{videoDuration}-second clip selected</p>

      </div>
      {videoState.progress !== null ? (
        <div className="w-full max-w-xs space-y-1.5">
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-rose-500 to-orange-400 rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(videoState.progress * 100, 99)}%` }}
            />
          </div>
          <p className="text-xs text-stone-400 text-right">{Math.round(videoState.progress * 100)}%</p>
        </div>
      ) : (
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      )}
      <p className="text-xs text-stone-400 flex items-center gap-1.5">
        <i className="ri-information-line text-stone-300" />
        Please keep this window open while generating
      </p>
    </div>
  );
}

/* ─── Image Completed View ─────────────────────────────────────────────────── */
function ImageCompletedView({
  imageUrl,
  onDownload,
  onStartOver,
}: {
  imageUrl: string;
  onDownload: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5 bg-green-50 border border-green-200 text-green-700 px-3 py-2.5 rounded-xl">
        <i className="ri-checkbox-circle-fill text-green-500 text-base shrink-0" />
        <span className="text-xs font-semibold flex-1">Your creative image is ready!</span>
      </div>
      <div className="rounded-xl overflow-hidden border border-stone-200 bg-stone-50">
        <img
          src={imageUrl}
          alt="Generated ad"
          className="w-full max-h-80 object-contain object-top"
        />
      </div>
      <button
        onClick={onDownload}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 text-white text-sm font-bold hover:from-stone-800 hover:to-stone-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-download-2-line text-base" />
        Download Image
      </button>
      <button
        onClick={onStartOver}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-200 text-stone-600 text-sm font-bold hover:bg-stone-50 cursor-pointer transition-all whitespace-nowrap"
      >
        <i className="ri-add-circle-line" /> Generate Another
      </button>
    </div>
  );
}

/* ─── Video Completed View ─────────────────────────────────────────────────── */
function VideoCompletedView({
  videoUrl,
  audioUrl,
  voiceoverScript,
  audioMode,
  voiceoverDraft,
  setVoiceoverDraft,
  onRegenerateVoiceover,
  isRegeneratingVoiceover,
  voiceoverEditError,
  imageUrl,
  motionLabel,
  aspectLabel,
  aspectRatio,
  isTextToVideo,
  onDownload,
  onRegenerateVideo,
  onStartOver,
  onOpenAudioStudio,
  onEditVideo,
  canAffordRegen,
  creditsRemaining,
}: {
  videoUrl: string;
  audioUrl: string | null;
  voiceoverScript: string | null;
  audioMode: VideoAudioMode;
  voiceoverDraft: string;
  setVoiceoverDraft: (value: string) => void;
  onRegenerateVoiceover: (script: string) => void;
  isRegeneratingVoiceover: boolean;
  voiceoverEditError: string | null;
  imageUrl: string | null;
  motionLabel: string;
  aspectLabel: string;
  aspectRatio: string;
  isTextToVideo: boolean;
  onDownload: () => void;
  onRegenerateVideo: () => void;
  onStartOver: () => void;
  onOpenAudioStudio: () => void;
  onEditVideo: () => void;
  canAffordRegen: boolean;
  creditsRemaining: number;
}) {
  const canAffordVoiceover = creditsRemaining >= VIDEO_AD_VOICEOVER_CREDITS;
  const isVoiceover = audioMode === 'voiceover';
  const audioLabel = isVoiceover ? 'Voiceover' : 'Sound effect';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5 bg-green-50 border border-green-200 text-green-700 px-3 py-2.5 rounded-xl">
        <i className="ri-checkbox-circle-fill text-green-500 text-base shrink-0" />
        <span className="text-xs font-semibold flex-1">Your video ad is ready!</span>
        <span className="text-xs text-green-500">
          {motionLabel} · {aspectLabel} ({aspectRatio})
        </span>
      </div>
      <div className="rounded-xl overflow-hidden bg-black border border-stone-200">
        <video
          src={videoUrl}
          controls
          autoPlay
          loop
          playsInline
          className="w-full max-h-72 object-contain"
        />
      </div>
      {audioUrl && (
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 space-y-2.5">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500 text-white shrink-0">
              <i className="ri-mic-line text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-orange-800">{audioLabel} generated</p>
              {isVoiceover && voiceoverScript && (
                <p className="text-xs text-orange-700 mt-1 leading-relaxed">{voiceoverScript}</p>
              )}
            </div>
          </div>
          {isVoiceover && (
          <div className="space-y-2">
            <label className="flex items-center justify-between text-xs font-bold text-orange-800">
              Edit script
              <span className="text-orange-500 font-semibold">{voiceoverDraft.length}/260</span>
            </label>
            <textarea
              value={voiceoverDraft}
              onChange={(e) => setVoiceoverDraft(e.target.value.slice(0, 260))}
              rows={3}
              className="w-full resize-none rounded-lg border border-orange-200 bg-white px-3 py-2.5 text-xs text-stone-800 placeholder:text-orange-300 focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100 transition-all leading-relaxed"
              placeholder="Edit the voiceover script before regenerating the audio..."
            />
            {voiceoverEditError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <i className="ri-error-warning-line shrink-0" />
                <span>{voiceoverEditError}</span>
              </div>
            )}
          </div>
          )}
          <audio src={audioUrl} controls className="w-full h-9" />
          <div className="grid grid-cols-2 gap-2">
            {isVoiceover && (
              <button
                onClick={() => onRegenerateVoiceover(voiceoverDraft)}
                disabled={isRegeneratingVoiceover || !voiceoverDraft.trim() || !canAffordVoiceover}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap ${
                  isRegeneratingVoiceover || !voiceoverDraft.trim() || !canAffordVoiceover
                    ? 'bg-orange-100 text-orange-300 cursor-not-allowed'
                    : 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer'
                }`}
              >
                <i className={isRegeneratingVoiceover ? 'ri-loader-4-line animate-spin' : 'ri-mic-2-line'} />
                {isRegeneratingVoiceover ? 'Regenerating...' : !canAffordVoiceover ? 'Need 10 Credits' : 'Regenerate Voice'}
              </button>
            )}
            <button
              onClick={() => downloadFile(audioUrl, `adforge-voiceover-${Date.now()}.mp3`)}
              className={`${isVoiceover ? '' : 'col-span-2'} flex items-center justify-center gap-2 py-2.5 rounded-lg border border-orange-200 text-orange-700 bg-white text-xs font-bold hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap`}
            >
              <i className="ri-download-2-line" />
              Download MP3
            </button>
          </div>
        </div>
      )}
      {imageUrl && !isTextToVideo && (
        <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl p-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-stone-200">
            <img src={imageUrl} alt="Source" className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-stone-800">Source Image</p>
            <p className="text-xs text-stone-400 mt-0.5">Generated from your campaign</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onOpenAudioStudio}
          className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-orange-200 bg-orange-50 text-orange-700 text-sm font-bold hover:bg-orange-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-sound-module-line text-base" />
          Audio Studio
        </button>
        <button
          onClick={onDownload}
          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 text-white text-sm font-bold hover:from-stone-800 hover:to-stone-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-2-line text-base" />
          Download
        </button>
        <button
          onClick={onEditVideo}
          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-orange-400 text-white text-sm font-bold hover:from-rose-600 hover:to-orange-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-2-line text-base" />
          Edit Video
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onRegenerateVideo}
          disabled={!canAffordRegen}
          className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-xs font-bold transition-all duration-200 whitespace-nowrap ${
            canAffordRegen
              ? 'border-orange-300 text-orange-600 bg-orange-50 hover:bg-orange-100 cursor-pointer'
              : 'border-stone-200 text-stone-400 cursor-not-allowed bg-stone-50'
          }`}
        >
          <i className="ri-refresh-line text-base" />
          <span>Regenerate Video</span>
          <span className={`font-normal ${canAffordRegen ? 'text-orange-400' : 'text-stone-400'}`}>
            {VIDEO_AD_REGENERATE_CREDITS} credits
          </span>
        </button>
        <button
          onClick={onStartOver}
          className="flex flex-col items-center gap-1 py-3 rounded-xl border-2 border-stone-200 text-stone-600 bg-stone-50 hover:bg-stone-100 text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-circle-line text-base" />
          <span>New Creative</span>
          <span className="font-normal text-stone-400">Start fresh</span>
        </button>
      </div>
      {!canAffordRegen && (
        <p className="text-xs text-stone-400 text-center">
          Balance: <strong className="text-stone-600">{creditsRemaining} credits</strong> — need {VIDEO_AD_REGENERATE_CREDITS} to regenerate.{' '}
          <Link to="/pricing" className="text-orange-500 underline underline-offset-2">Upgrade</Link>
        </p>
      )}
    </div>
  );
}

/* ─── Failed View ──────────────────────────────────────────────────────────── */
function FailedView({
  error,
  onRetry,
  onStartOver,
  onClose,
  isImageError,
  canAfford,
  creditsRefunded,
  refundedAmount,
}: {
  error: string | null;
  onRetry: () => void;
  onStartOver: () => void;
  onClose: () => void;
  isImageError: boolean;
  canAfford: boolean;
  creditsRefunded?: boolean;
  refundedAmount?: number;
}) {
  const isRateLimit = error?.toLowerCase().includes('rate limit') ||
                      error?.toLowerCase().includes('429') ||
                      error?.toLowerCase().includes('wait') ||
                      error?.toLowerCase().includes('cooldown');

  return (
    <div className="flex flex-col items-center gap-5 py-8 text-center">
      <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${isRateLimit ? 'bg-amber-100' : 'bg-red-100'}`}>
        <i className={`${isRateLimit ? 'ri-time-line text-amber-500' : 'ri-error-warning-line text-red-500'} text-2xl`} />
      </div>
      <div>
        <p className="text-sm font-bold text-stone-900">
          {isRateLimit ? 'Please wait a moment' : (isImageError ? 'Image generation failed' : 'Video generation failed')}
        </p>
        <p className="text-xs text-stone-400 mt-1.5 max-w-xs mx-auto leading-relaxed">
          {error ?? 'Something went wrong. Please try again.'}
        </p>
        {isRateLimit && (
          <p className="text-xs text-amber-600 mt-2 max-w-xs mx-auto leading-relaxed">
            The AI service is temporarily busy. You can try again in a few moments.
          </p>
        )}
      </div>

      {/* Credits refunded notice */}
      {creditsRefunded && refundedAmount && (
        <div className="w-full max-w-xs flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-100 shrink-0">
            <i className="ri-coin-line text-green-600 text-sm" />
          </div>
          <div className="text-left">
            <p className="text-xs font-bold text-green-800">Credits refunded</p>
            <p className="text-xs text-green-600 mt-0.5">
              {refundedAmount} credits have been returned to your account.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 w-full max-w-xs">
        {(canAfford || isRateLimit) && (
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-orange-400 text-white text-sm font-bold hover:from-rose-600 hover:to-orange-500 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> Try Again
          </button>
        )}
        <button
          onClick={onStartOver}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-200 text-stone-600 text-sm font-bold hover:bg-stone-50 cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" /> Back to Settings
        </button>
        <button
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-stone-400 text-sm font-medium hover:text-stone-600 cursor-pointer transition-all whitespace-nowrap"
        >
          Close
        </button>
      </div>
    </div>
  );
}
